import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import MfaSetup from '@/components/MfaSetup';
import LoadingState from '@/components/LoadingState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, AlertCircle, CheckCircle } from 'lucide-react';

const ResetMfa = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [resetCompleted, setResetCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token') ? decodeURIComponent(searchParams.get('token')!) : null;

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid reset link - missing token');
        setLoading(false);
        return;
      }

      try {
        // Validate the reset token using the security definer function
        const { data: validationResult, error: tokenError } = await (supabase
          .rpc as any)('validate_mfa_reset_token', { reset_token_input: token });

        if (tokenError) {
          console.error('Error validating reset token:', tokenError);
          setError('Failed to validate reset link');
          setLoading(false);
          return;
        }

        const result = validationResult?.[0];
        if (!result?.is_valid) {
          setError('Invalid or expired reset link');
          setLoading(false);
          return;
        }

        setUserEmail(result.target_user_email);
        setTokenValid(true);
      } catch (error) {
        console.error('Error validating reset token:', error);
        setError('Failed to validate reset link');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleMfaSetupComplete = async () => {
    try {
      // Mark the reset action as completed
      const { error: updateError } = await supabase
        .from('admin_mfa_reset_actions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('reset_token', token);

      if (updateError) {
        console.error('Error updating reset status:', updateError);
      }

      sessionStorage.setItem('figabl_mfa_completed', 'true');
      sessionStorage.setItem('figabl_mfa_reset_completed', 'true');

      // Load organization data for this user
      try {
        const { data: orgData, error: orgError } = await supabase
          .from('organisations')
          .select('*')
          .eq('poc_email', userEmail)
          .limit(1);

        if (orgError) {
          console.error('Error loading organization data:', orgError);
        } else if (orgData && orgData.length > 0) {
          const organization = orgData[0];
          
          // Store the organization data in session storage
          const companyData = {
            name: organization.name,
            address: organization.address,
            pocName: organization.poc_name,
            pocEmail: organization.poc_email,
            pocPhone: organization.poc_phone,
            pocPhoneVerified: true,
            ein: organization.ein,
            stateOfIncorporation: organization.state_of_incorporation,
            nameDbas: organization.name_dbas,
            products: organization.products || [],
            terms: true,
            organization_id: organization.id,
            connections: organization.connections
          };
          
          // Process connections if they exist
          if (organization.connections) {
            try {
              const { parseConnections } = await import('@/utils/jsonHelpers');
              const connections = parseConnections(organization.connections);
              
              // Separate CSV and regular connections
              const csvConnections = connections.filter(conn => conn.csvImports && Array.isArray(conn.csvImports) && conn.csvImports.length > 0);
              const regularConnections = connections.filter(conn => !conn.csvImports || !Array.isArray(conn.csvImports) || conn.csvImports.length === 0);
              
              // Store connection IDs in session storage
              if (regularConnections.length > 0) {
                sessionStorage.setItem('figabl_connection_ids', JSON.stringify(regularConnections.map(c => c.id)));
                sessionStorage.setItem('figabl_connection_id', regularConnections[0].id);
              }
              
              if (csvConnections.length > 0) {
                sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csvConnections.map(c => c.id)));
              }
              
            } catch (parseError) {
              console.error('Error parsing connections:', parseError);
            }
          } 
          
          sessionStorage.setItem('savedFormStep', '1');
          sessionStorage.setItem('figabl_company_data', JSON.stringify(companyData));
        }
      } catch (orgError) {
        console.error('Error fetching organization data:', orgError);
      }

      setResetCompleted(true);
      
      toast({
        title: 'MFA Reset Complete',
        description: 'Your new MFA setup has been successfully configured.',
      });

      // Redirect to the form after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Error completing MFA reset:', error);
      toast({
        title: 'Warning',
        description: 'MFA was set up but there was an issue updating the reset status.',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    navigate('/login');
  };

  if (loading) {
    return <LoadingState message="Validating reset link..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto flex items-center justify-center py-8 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle>Invalid Reset Link</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">{error}</p>
              <p className="text-sm text-muted-foreground">
                Reset links expire after 24 hours. If you need a new reset link, 
                please contact your administrator.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (resetCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto flex items-center justify-center py-8 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle>MFA Reset Complete</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Your new MFA setup has been successfully configured.
              </p>
              <p className="text-sm text-muted-foreground">
                You will be redirected to continue with your form shortly...
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-md space-y-6">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Shield className="h-6 w-6 text-amber-600" />
              </div>
              <CardTitle>Reset Your MFA</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">
                Your administrator has initiated an MFA reset for your account.
                Please set up a new phone number for SMS verification.
              </p>
              <p className="text-sm text-muted-foreground">
                Email: <span className="font-mono">{userEmail}</span>
              </p>
            </CardContent>
          </Card>

          <MfaSetup 
            onComplete={handleMfaSetupComplete}
            userEmail={userEmail}
            isReset={true}
          />
        </div>
      </main>
      
      <footer className="bg-white py-4 border-t">
        <div className="container mx-auto px-4">
          <p className="text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Figabl. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ResetMfa;